import React, { useState, useRef, useEffect } from 'react';
import Footer from './Footer';
import SearchResults from './SearchResults';
import Notifications from './Notifications';
import './ContactPage.css';
import { API, authHeadersOnly } from '../services/apiConfig';

const QUERY_OPTIONS = [
  'Order Status', 'Delivery Issue', 'Missing Items',
  'Damaged / Defective Product', 'Incorrect Order',
  'Invoice / Billing Query', 'Other',
];

export default function ContactPage({ user, onLogout, onCartClick, onShowOrders, onShowAccountDashboard, onProductClick }) {
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', invoiceNo: '', queryType: '', description: '' });
  const [imageFiles, setImageFiles]     = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showNotif, setShowNotif]   = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef();

  useEffect(() => {
    if (!user?.userId) return;
    const fetch_ = () =>
      fetch(`${API}/api/notifications/user/${user.userId}/unread-count`, { headers: authHeadersOnly() })
        .then(r => r.ok ? r.json() : 0).then(setNotifCount).catch(() => {});
    fetch_();
    const t = setInterval(fetch_, 30000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFieldErrors(p => ({...p, [e.target.name]: ''}));
  };

  const handleImageSelect = e => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const newFiles = [...imageFiles, ...files].slice(0, 6);
    setImageFiles(newFiles);
    setImagePreviews(newFiles.map(f => URL.createObjectURL(f)));
    e.target.value = '';
  };

  const handleRemoveImage = idx => {
    const newFiles = imageFiles.filter((_, i) => i !== idx);
    setImageFiles(newFiles);
    setImagePreviews(newFiles.map(f => URL.createObjectURL(f)));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!user) {
      setToast({ message: 'Please log in to submit a query.', type: 'error' });
      return;
    }
    if (!form.queryType) { setError('Please select a query type'); return; }
    const fe = {};
    if (!form.email.includes('@')) fe.email = 'Email must contain @';
    if (form.phone && !/^[+]?[0-9]{10,15}$/.test(form.phone.replace(/\s/g,''))) fe.phone = 'Enter a valid phone number';
    if (Object.keys(fe).length > 0) { setFieldErrors(fe); return; }
    setFieldErrors({});
    setSubmitting(true); setError('');
    try {
      const fd = new FormData();
      fd.append('userId', user.userId);
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (imageFiles.length) imageFiles.forEach(f => fd.append('images', f));
      const res = await fetch(`${API}/api/contact`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error();
      setSubmitted(true);
      setForm({ fullName: '', email: '', phone: '', invoiceNo: '', queryType: '', description: '' });
      setImageFiles([]); setImagePreviews([]);
    } catch { setError('Failed to submit query. Please try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="cp-root">

      {/* ── Hero: full-width image, navbar floats inside with L/R margin ── */}
      <div className="cp-hero-wrap" style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.52), rgba(0,0,0,0.52)), url('/images/contact pg top banner.jpg.jpeg')`
      }}>

        {/* Exact compact navbar replica — full-width on mobile via CSS */}
        <div className="cp-compact-nav">
          <div className="cp-compact-inner">

            <a href="/" className="compact-logo" style={{ textDecoration: 'none' }}>
              <img src="/images/corgif-logo.png" alt="corgif" />
            </a>

            <nav className="compact-nav cp-desktop-nav">
              <a href="/"        className="compact-nav-link">Home</a>
              <a href="/products" className="compact-nav-link">Shop</a>
              <a href="/#why-corgif" className="compact-nav-link">About Corgif</a>
              <a href="/contact"  className="compact-nav-link cp-active-link">Contact</a>
            </nav>

            <div className="compact-search-container cp-desktop-search">
              <SearchResults onProductClick={onProductClick} />
            </div>

            <div className="compact-actions cp-desktop-actions">
              {user ? (
                <div className="compact-user-menu">
                  <a href="#" className="compact-icon-btn" style={{ position: 'relative' }}
                    onClick={e => { e.preventDefault(); setShowNotif(s => !s); }}>
                    <i className="fas fa-bell"></i>
                    {notifCount > 0 && <span className="compact-notification-badge">{notifCount}</span>}
                  </a>
                  <div className="compact-profile-dropdown">
                    <a href="#" className="compact-icon-btn">
                      <i className="fas fa-user-circle"></i>
                    </a>
                    <div className="compact-dropdown-menu">
                      <a href="#" onClick={e => { e.preventDefault(); onCartClick(); }} className="compact-dropdown-item">
                        <i className="fas fa-shopping-cart" style={{ color: '#9A1111' }}></i>
                        <span style={{ color: '#9A1111' }}>Cart</span>
                      </a>
                      <a href="#" onClick={e => { e.preventDefault(); onShowAccountDashboard(); }} className="compact-dropdown-item">
                        <i className="fas fa-user-circle"></i> Account
                      </a>
                      <a href="#" onClick={e => { e.preventDefault(); onShowOrders(); }} className="compact-dropdown-item">
                        <i className="fas fa-history"></i> Orders
                      </a>
                      <a href="#" onClick={e => { e.preventDefault(); onLogout(); }} className="compact-dropdown-item logout">
                        <i className="fas fa-sign-out-alt"></i> Logout
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <a href="/login" className="compact-login-btn">Signup / Login</a>
              )}
            </div>

            {/* Mobile-only right side */}
            <div className="cp-mobile-actions">
              {user && (
                <a href="#" className="mobile-topbar-icon" style={{ position: 'relative' }}
                  onClick={e => { e.preventDefault(); setShowNotif(s => !s); }}>
                  <i className="fas fa-bell"></i>
                  {notifCount > 0 && <span className="notification-badge">{notifCount}</span>}
                </a>
              )}
              <button className="mobile-topbar-hamburger" onClick={() => setShowNotif(false)}
                style={{ display: 'none' }} aria-label="Menu" />
              <a href="/" className="cp-mobile-back">
                <i className="fas fa-home"></i>
              </a>
              {!user && <a href="/login" className="compact-login-btn" style={{ fontSize: '12px', padding: '6px 14px' }}>Login</a>}
            </div>

          </div>
        </div>

        {/* Hero title */}
        <div className="cp-hero">
          <h1>Contact &amp; Query</h1>
        </div>
      </div>

      {/* ── Page content ── */}
      <div className="cp-container">

        <div className="cp-title">
          <h2>Raise a Query</h2>
          <p>Facing an issue with your order or need any assistance? We're here to help.</p>
        </div>

        {submitted ? (
          <div className="cp-success">
            <div className="cp-success-icon">✓</div>
            <h3>Query Submitted Successfully!</h3>
            <p>Our team will get back to you within 24 hours.</p>
            <button className="cp-submit-btn" onClick={() => setSubmitted(false)}>Submit Another Query</button>
          </div>
        ) : (
          <form className="cp-form-grid" onSubmit={handleSubmit}>

            <div className="cp-field cp-name">
              <label>Full Name<span>*</span></label>
              <input name="fullName" placeholder="Rahul Sharma" value={form.fullName} onChange={handleChange} required />
            </div>
            <div className="cp-field cp-email">
              <label>Email ID<span>*</span></label>
              <input name="email" type="text" placeholder="rahul@gmail.com" value={form.email} onChange={handleChange} required />
              {fieldErrors.email && <p style={{ color: '#c0392b', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.email}</p>}
            </div>
            <div className="cp-field cp-phone">
              <label>Phone Number</label>
              <input name="phone" placeholder="+91 XXXXX XXXXX" value={form.phone} onChange={handleChange} />
              {fieldErrors.phone && <p style={{ color: '#c0392b', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.phone}</p>}
            </div>
            <div className="cp-field cp-invoice">
              <label>Invoice No.<span>*</span></label>
              <input name="invoiceNo" placeholder="INV-B4B760CA" value={form.invoiceNo} onChange={handleChange} required />
            </div>

            <div className="cp-field cp-query">
              <label>Query Type<span>*</span></label>
              <div className="cp-select-wrap">
                <div className={`cp-select-trigger ${form.queryType ? 'selected' : ''}`}>
                  {form.queryType || 'Select type of query'}
                </div>
                <div className="cp-select-dropdown">
                  {QUERY_OPTIONS.map(opt => (
                    <div key={opt} className={`cp-select-option ${form.queryType === opt ? 'active' : ''}`}
                      onClick={() => setForm(f => ({ ...f, queryType: opt }))}>
                      {opt}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="cp-field cp-upload">
              <label>Upload Images (optional)</label>
              <div
                className={`cp-upload-box ${imagePreviews.length ? 'has-images' : ''}`}
                onClick={() => fileInputRef.current.click()}
              >
                {imagePreviews.length === 0 ? (
                  <>
                    <p className="cp-upload-text">Upload photo(s) of the product or packaging to help us review and resolve your issue faster.</p>
                    <div className="cp-upload-icon">
                      <i className="fa-regular fa-image"></i>
                      <i className="fa-solid fa-arrow-up cp-upload-arrow"></i>
                    </div>
                    <div className="cp-upload-btn">Upload from device</div>
                  </>
                ) : (
                  <div className="cp-preview-grid" onClick={e => e.stopPropagation()}>
                    {imagePreviews.map((src, idx) => (
                      <div key={idx} className="cp-preview-item">
                        <img src={src} alt={`preview-${idx}`} />
                        <button
                          type="button"
                          className="cp-preview-remove"
                          onClick={() => handleRemoveImage(idx)}
                          aria-label="Remove image"
                        >✕</button>
                      </div>
                    ))}
                    {imagePreviews.length < 6 && (
                      <div
                        className="cp-preview-add"
                        onClick={() => fileInputRef.current.click()}
                      >
                        <i className="fa-solid fa-plus"></i>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleImageSelect} />
              <p className="cp-note">Please include accurate details for faster assistance.<br />Our team typically responds within 24 hours.</p>
            </div>

            <div className="cp-field cp-textarea">
              <label>Describe Your Issue<span>*</span></label>
              <textarea name="description"
                placeholder="Please share details of your issue, including relevant dates, items, or concerns."
                value={form.description} onChange={handleChange} required />
            </div>

            {error && <p className="cp-error">{error}</p>}
            <button type="submit" className="cp-submit-btn" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Query'}
            </button>

          </form>
        )}

        <div className="cp-cards">
          {[
            { icon: 'fas fa-phone',         text: '+91 9823666128' },
            { icon: 'fab fa-whatsapp',       text: '+91 9823666128' },
            { icon: 'fas fa-envelope',       text: 'info@chalkwalkconsulting.com' },
            { icon: 'fas fa-map-marker-alt', text: '402, Gulmohar Orchids, Kharadi - Pune,Maharashtra,India 411014' },
          ].map((c, i) => (
            <div key={i} className="cp-card">
              <div className="cp-card-icon"><i className={c.icon}></i></div>
              <p style={{ whiteSpace: 'pre-line' }}>{c.text}</p>
            </div>
          ))}
        </div>

      </div>

     

      <Footer />

      {toast && (
        <div style={{
          position: 'fixed', bottom: '32px', right: '32px', zIndex: 99999,
          background: toast.type === 'error' ? '#9A1111' : '#16a34a',
          color: 'white', padding: '28px 24px', borderRadius: '25px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px',
          minWidth: '280px', maxWidth: '320px', textAlign: 'center',
          animation: 'toastIn 0.35s cubic-bezier(0.34,1.56,0.64,1)',
        }}>
          <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px' }}>
            {toast.type === 'error' ? 'ℹ' : '✓'}
          </div>
          <span style={{ fontWeight: '600', fontSize: '15px', lineHeight: '1.5' }}>{toast.message}</span>
          <style>{`@keyframes toastIn { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
        </div>
      )}

      {showNotif && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10000 }}
            onClick={() => { setShowNotif(false); }} />
          <div style={{ position: 'fixed', top: '70px', right: '20px', zIndex: 10001 }}>
            <Notifications userId={user?.userId} onClose={() => setShowNotif(false)} isDropdown={true} />
          </div>
        </>
      )}
    </div>
  );
}
